import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Activity, ArrowRight, Briefcase, FileText, Mail, UserCheck, Users, Bell } from "lucide-react";
import { AdminNav } from "@/components/admin/AdminNav";
import { AdminLoading, useAdminAuth } from "@/components/admin/useAdminAuth";

const API = process.env.REACT_APP_BACKEND_URL;

const CARDS = [
    ["recruitment_enquiries", "Recruitment Enquiries", FileText, "/admin/recruitment-requests", "recruitment"],
    ["contact_enquiries", "Contact Enquiries", Mail, "/admin/contact-enquiries", "contact"],
    ["job_postings", "Job Postings", Briefcase, "/admin/jobs", "jobs"],
    ["candidate_applications", "Candidate Applications", Users, "/admin/applications", "applications"],
    ["shortlisted_candidates", "Shortlisted", UserCheck, "/admin/applications?status=shortlisted", "applications"],
    ["interview_candidates", "Interviews", Activity, "/admin/applications?status=interview", "applications"],
    ["selected_candidates", "Selected", UserCheck, "/admin/applications?status=selected", "applications"],
    ["job_alerts", "Job Alerts", Bell, "/admin/alerts", "alerts"],
];

const formatDate = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};

const actionLabel = (action = "") => action.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function AdminDashboard() {
    const { user, logout, hasPermission } = useAdminAuth();
    const [data, setData] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!user) return;
        (async () => {
            try {
                const res = await fetch(`${API}/api/admin/dashboard`, { credentials: "include" });
                if (!res.ok) throw new Error();
                setData(await res.json());
            } catch {
                setError("Could not load the dashboard. Please try again.");
            }
        })();
    }, [user]);

    if (!user) return <AdminLoading />;

    const metrics = data?.metrics || {};
    const visibleCards = CARDS.filter(([, , , , permission]) => hasPermission(permission));

    return (
        <div className="min-h-screen bg-[#F8FAFC] font-adminSans text-[#0F172A]">
            <AdminNav active="dashboard" email={user.email} user={user} onLogout={logout} />
            <main className="mx-auto max-w-[1500px] px-4 py-8 sm:px-6 lg:px-8" data-testid="admin-dashboard">
                <div className="flex flex-wrap items-end justify-between gap-5">
                    <div>
                        <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#D4AF37]">Admin Portal</p>
                        <h1 className="mt-2 font-adminDisplay text-4xl font-bold tracking-tight sm:text-5xl">Dashboard</h1>
                        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[#64748B]">
                            Operational overview across recruitment enquiries, jobs, candidates and administrator activity.
                        </p>
                    </div>
                    <div className="rounded-md border border-[#E2E8F0] bg-white px-5 py-4 text-sm text-[#64748B] shadow-sm">
                        Signed in as <span className="font-semibold text-[#0F172A]">{user.email}</span>
                        <span className="mt-1 block text-[10px] font-bold uppercase tracking-[0.18em] text-[#D4AF37]">{user.admin_role?.replace(/_/g, " ")}</span>
                    </div>
                </div>

                {error && <p data-testid="dashboard-error" className="mt-8 rounded-md border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">{error}</p>}

                <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4" data-testid="dashboard-metrics">
                    {visibleCards.map(([key, label, Icon, href, permission], index) => (
                        <motion.a
                            key={key}
                            href={href}
                            data-testid={`dashboard-card-${key}`}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: index * 0.04 }}
                            className="group rounded-lg border border-[#E2E8F0] bg-white p-6 shadow-sm transition-all duration-200 hover:-translate-y-[2px] hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                        >
                            <div className="flex items-center justify-between">
                                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-[#0B1120] text-[#D4AF37]">
                                    <Icon size={19} strokeWidth={1.7} />
                                </span>
                                <ArrowRight size={16} className="text-[#CBD5E1] transition-colors group-hover:text-[#D4AF37]" />
                            </div>
                            <p className="mt-6 text-3xl font-bold tracking-tight text-[#0F172A]">{metrics[key] ?? "—"}</p>
                            <p className="mt-1 text-xs font-bold uppercase tracking-[0.16em] text-[#64748B]">{label}</p>
                        </motion.a>
                    ))}
                </div>

                <div className="mt-8 grid gap-6 lg:grid-cols-[2fr_1fr]">
                    <section className="rounded-lg border border-[#E2E8F0] bg-white p-6 shadow-sm" data-testid="dashboard-recent-submissions">
                        <div className="flex items-center justify-between gap-4">
                            <div>
                                <h2 className="font-adminDisplay text-2xl font-semibold tracking-tight">Recent Enquiries</h2>
                                <p className="mt-1 text-sm text-[#64748B]">Latest recruitment and contact submissions.</p>
                            </div>
                            {hasPermission("recruitment") && (
                                <a href="/admin/recruitment-requests" className="text-xs font-bold uppercase tracking-[0.14em] text-[#0B1120] hover:text-[#D4AF37]">View all</a>
                            )}
                        </div>
                        <div className="mt-6 divide-y divide-[#E2E8F0]">
                            {(data?.recent_submissions || []).length === 0 ? (
                                <p className="py-8 text-sm text-[#64748B]">No recent enquiries yet.</p>
                            ) : (
                                data.recent_submissions.map((item) => (
                                    <a key={item.id} href={item.type === "recruitment" ? "/admin/recruitment-requests" : "/admin/contact-enquiries"} className="flex items-center justify-between gap-4 py-4 transition-colors hover:bg-[#F8FAFC]" data-testid={`recent-submission-${item.id}`}>
                                        <span className="min-w-0">
                                            <span className="block truncate text-sm font-semibold text-[#0F172A]">{item.type === "recruitment" ? item.company_name : item.name}</span>
                                            <span className="block truncate text-xs text-[#64748B]">{item.type === "recruitment" ? item.position : item.interest} · {item.email}</span>
                                        </span>
                                        <span className="shrink-0 text-xs text-[#94A3B8]">{formatDate(item.created_at)}</span>
                                    </a>
                                ))
                            )}
                        </div>
                    </section>

                    <section className="rounded-lg border border-[#E2E8F0] bg-white p-6 shadow-sm" data-testid="dashboard-activity-feed">
                        <h2 className="font-adminDisplay text-2xl font-semibold tracking-tight">Recent Activity</h2>
                        <p className="mt-1 text-sm text-[#64748B]">Security and administration audit events.</p>
                        <div className="mt-6 space-y-4">
                            {(data?.recent_activity || []).length === 0 ? (
                                <p className="py-8 text-sm text-[#64748B]">No audit activity recorded yet.</p>
                            ) : (
                                data.recent_activity.slice(0, 8).map((event) => (
                                    <div key={event.id} className="border-l-2 border-[#D4AF37]/50 pl-4" data-testid={`audit-event-${event.id}`}>
                                        <p className="text-sm font-semibold text-[#0F172A]">{actionLabel(event.action)}</p>
                                        <p className="mt-1 text-xs text-[#64748B]">{event.actor_email || event.actor_id} · {formatDate(event.created_at)}</p>
                                    </div>
                                ))
                            )}
                        </div>
                    </section>
                </div>
            </main>
        </div>
    );
}
